import React from 'react';
import {AbsoluteFill, Img, Sequence, staticFile, useCurrentFrame, useVideoConfig} from 'remotion';

type Variant =
  | 'sliding-blocks'
  | 'checkerboard'
  | 'vertical-wipes'
  | 'horizontal-wipes'
  | 'corner-boxes'
  | 'audio-bars'
  | 'sequence-cuts'
  | 'image-static'
  | 'image-step-pan'
  | 'palette-swap';

type FcCompatCaseProps = {
  variant: Variant;
  image?: string;
  audio?: string;
};

const colors = ['#101820', '#005f73', '#0a9396', '#94d2bd', '#e9d8a6', '#ee9b00', '#ca6702', '#bb3e03', '#ae2012', '#9b2226'];

export const FcCompatCase: React.FC<FcCompatCaseProps> = ({
  variant,
  image = 'fc-compat/pixel-grid.png',
}) => {
  const frame = useCurrentFrame();
  const {width, height, durationInFrames} = useVideoConfig();

  if (variant === 'sliding-blocks') {
    return (
      <AbsoluteFill style={{backgroundColor: '#101820'}}>
        {Array.from({length: 8}).map((_, index) => {
          const left = ((frame * (index + 1) * 8 + index * 120) % (width + 160)) - 160;
          return <Rect key={index} x={left} y={index * 90} w={160} h={70} color={colors[(index + frame) % colors.length]} />;
        })}
      </AbsoluteFill>
    );
  }

  if (variant === 'checkerboard') {
    return (
      <AbsoluteFill style={{backgroundColor: '#000000'}}>
        {Array.from({length: 16 * 9}).map((_, index) => {
          const x = index % 16;
          const y = Math.floor(index / 16);
          const active = (x + y + Math.floor(frame / 10)) % 2 === 0;
          return <Rect key={index} x={x * 80} y={y * 80} w={80} h={80} color={active ? '#ffffff' : colors[(x + y) % colors.length]} />;
        })}
      </AbsoluteFill>
    );
  }

  if (variant === 'vertical-wipes') {
    const step = Math.floor((frame / Math.max(1, durationInFrames - 1)) * 16);
    return (
      <AbsoluteFill style={{backgroundColor: '#1e1b4b'}}>
        {Array.from({length: 16}).map((_, index) => (
          <Rect key={index} x={index * 80} y={0} w={80} h={height} color={index <= step ? colors[index % colors.length] : '#1e1b4b'} />
        ))}
      </AbsoluteFill>
    );
  }

  if (variant === 'horizontal-wipes') {
    const step = Math.floor((frame / Math.max(1, durationInFrames - 1)) * 9);
    return (
      <AbsoluteFill style={{backgroundColor: '#052e16'}}>
        {Array.from({length: 9}).map((_, index) => (
          <Rect key={index} x={0} y={index * 80} w={width} h={80} color={index <= step ? colors[(index + 3) % colors.length] : '#052e16'} />
        ))}
      </AbsoluteFill>
    );
  }

  if (variant === 'corner-boxes') {
    const size = 80 + (Math.floor(frame / 8) % 5) * 40;
    return (
      <AbsoluteFill style={{backgroundColor: '#f8fafc'}}>
        <Rect x={0} y={0} w={size} h={size} color="#ef4444" />
        <Rect x={width - size} y={0} w={size} h={size} color="#22c55e" />
        <Rect x={0} y={height - size} w={size} h={size} color="#3b82f6" />
        <Rect x={width - size} y={height - size} w={size} h={size} color="#f59e0b" />
        <Rect x={width / 2 - size / 2} y={height / 2 - size / 2} w={size} h={size} color="#111827" />
      </AbsoluteFill>
    );
  }

  if (variant === 'audio-bars') {
    const phase = Math.floor(frame / 12);
    return (
      <AbsoluteFill style={{backgroundColor: '#111827'}}>
        {Array.from({length: 8 * 6}).map((_, index) => {
          const x = index % 8;
          const y = Math.floor(index / 8);
          const active = (x * 2 + y * 3 + phase) % colors.length;
          return <Rect key={index} x={x * 160} y={y * 120} w={160} h={120} color={colors[active]} />;
        })}
      </AbsoluteFill>
    );
  }

  if (variant === 'sequence-cuts') {
    return (
      <AbsoluteFill style={{backgroundColor: '#000000'}}>
        <Sequence from={0} durationInFrames={40}>
          <AbsoluteFill style={{backgroundColor: '#dc2626'}} />
        </Sequence>
        <Sequence from={40} durationInFrames={40}>
          <AbsoluteFill style={{backgroundColor: '#16a34a'}} />
        </Sequence>
        <Sequence from={80} durationInFrames={40}>
          <AbsoluteFill style={{backgroundColor: '#2563eb'}} />
        </Sequence>
      </AbsoluteFill>
    );
  }

  if (variant === 'image-static') {
    return (
      <AbsoluteFill style={{backgroundColor: '#000000'}}>
        <Img src={staticFile(image)} style={{width: 1280, height: 720, imageRendering: 'pixelated'}} />
      </AbsoluteFill>
    );
  }

  if (variant === 'image-step-pan') {
    const x = -((Math.floor(frame / 10) % 5) * 40);
    return (
      <AbsoluteFill style={{backgroundColor: '#000000', overflow: 'hidden'}}>
        <Img src={staticFile(image)} style={{width: 1600, height: 720, transform: `translateX(${x}px)`, imageRendering: 'pixelated'}} />
      </AbsoluteFill>
    );
  }

  return (
    <AbsoluteFill style={{backgroundColor: colors[Math.floor(frame / 12) % colors.length]}}>
      {Array.from({length: 10}).map((_, index) => (
        <Rect key={index} x={index * 128} y={(index % 2) * 360} w={128} h={360} color={colors[(index + Math.floor(frame / 12)) % colors.length]} />
      ))}
    </AbsoluteFill>
  );
};

const Rect: React.FC<{x: number; y: number; w: number; h: number; color: string}> = ({x, y, w, h, color}) => (
  <div style={{position: 'absolute', left: Math.round(x), top: Math.round(y), width: Math.round(w), height: Math.round(h), backgroundColor: color}} />
);

const common = {
  component: FcCompatCase,
  durationInFrames: 120,
  fps: 30,
  width: 1280,
  height: 720,
} as const;

export const fcCompatibilityCases = [
  {id: 'FcCompatSlidingBlocks', ...common, defaultProps: {variant: 'sliding-blocks'} satisfies FcCompatCaseProps},
  {id: 'FcCompatCheckerboard', ...common, defaultProps: {variant: 'checkerboard'} satisfies FcCompatCaseProps},
  {id: 'FcCompatVerticalWipes', ...common, defaultProps: {variant: 'vertical-wipes'} satisfies FcCompatCaseProps},
  {id: 'FcCompatHorizontalWipes', ...common, defaultProps: {variant: 'horizontal-wipes'} satisfies FcCompatCaseProps},
  {id: 'FcCompatCornerBoxes', ...common, defaultProps: {variant: 'corner-boxes'} satisfies FcCompatCaseProps},
  {id: 'FcCompatAudioBars', ...common, defaultProps: {variant: 'audio-bars'} satisfies FcCompatCaseProps},
  {id: 'FcCompatSequenceCuts', ...common, defaultProps: {variant: 'sequence-cuts'} satisfies FcCompatCaseProps},
  {id: 'FcCompatImageStatic', ...common, defaultProps: {variant: 'image-static'} satisfies FcCompatCaseProps},
  {id: 'FcCompatImageStepPan', ...common, defaultProps: {variant: 'image-step-pan'} satisfies FcCompatCaseProps},
  {id: 'FcCompatPaletteSwap', ...common, defaultProps: {variant: 'palette-swap'} satisfies FcCompatCaseProps},
] as const;
